import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';

// NIP-71 versions (+ 'note-video' for kind 1 notes linking video files)
export type NIP71Version = 'nip71-regular' | 'nip71-addressable' | 'nip71-legacy' | 'note-video';

// Video event kinds per NIP-71
export const VIDEO_KINDS = {
  // Regular events (non-replaceable)
  regular: {
    horizontal: 21,
    vertical: 22,
  },
  // Addressable events (replaceable, require 'd' tag)
  addressable: {
    horizontal: 34235,
    vertical: 34236,
  },
} as const;

// All supported video kinds
export const ALL_VIDEO_KINDS = [21, 22, 34235, 34236];

/** Matches URLs ending in a video file extension (optionally with query). */
const VIDEO_URL_RE = /https?:\/\/[^\s"'<>()\[\]]+?\.(?:mp4|webm|mov|m4v|mkv|avi|ogv|m3u8)(?:\?[^\s"'<>()\[\]]*)?/i;

/** Extract the first video file URL from free text (e.g. a kind 1 note). */
export function extractVideoUrlFromText(text: string): string | null {
  const match = text.match(VIDEO_URL_RE);
  return match ? match[0] : null;
}

/** True when the kind 1 event is a reply (NIP-10 e tags) rather than a top-level post. */
function isReplyNote(event: NostrEvent): boolean {
  return event.tags.some(t => t[0] === 'e' && !!t[1]);
}

/**
 * Parse a kind 1 note that links a video file (in an imeta tag or directly
 * in the content) into VideoData. Returns null for notes without video.
 * Replies are excluded so the Notes feed only shows top-level posts.
 */
export function parseNoteVideoEvent(event: NostrEvent): VideoData | null {
  if (event.kind !== 1) return null;
  if (isReplyNote(event)) return null;

  let videoUrl = '';
  let thumbnailUrl: string | undefined;
  let duration: number | undefined;
  let dimensions: { width: number; height: number } | undefined;
  let mimeType: string | undefined;

  // Prefer an imeta attachment with a video MIME type (NIP-92 style)
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    const imeta = parseImetaTag(tag);
    const url = imeta.url?.[0];
    if (!url) continue;
    const m = imeta.m?.[0];
    const isVideo = (m && (m.startsWith('video/') || isHlsMimeType(m))) || extractVideoUrlFromText(url) !== null;
    if (!isVideo) continue;
    videoUrl = url;
    mimeType = m;
    thumbnailUrl = imeta.image?.[0];
    if (imeta.duration?.[0]) {
      const d = parseFloat(imeta.duration[0]);
      if (Number.isFinite(d) && d > 0) duration = d;
    }
    if (imeta.dim?.[0]) {
      const [w, h] = imeta.dim[0].split('x').map(Number);
      if (w && h) dimensions = { width: w, height: h };
    }
    break;
  }

  // Otherwise scan the plaintext content for a video file URL
  if (!videoUrl) {
    const found = extractVideoUrlFromText(event.content);
    if (!found) return null;
    videoUrl = found;
  }

  // Title: content with the URL removed, first line, trimmed
  const textWithoutUrl = event.content.replace(videoUrl, '').trim();
  const firstLine = textWithoutUrl.split('\n').map(l => l.trim()).find(l => l.length > 0) ?? '';
  let title = firstLine.slice(0, 100);
  if (!title) {
    // Fall back to the file name
    try {
      const pathname = new URL(videoUrl).pathname;
      const file = decodeURIComponent(pathname.split('/').pop() ?? '');
      title = file.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
    } catch { /* ignore */ }
  }
  if (!title) title = 'Video note';

  const contentWarning = event.tags.find(t => t[0] === 'content-warning')?.[1];
  const hashtags = event.tags.filter(t => t[0] === 't').map(t => t[1]);

  const isHls = isHlsUrl(videoUrl) || isHlsMimeType(mimeType);

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    title,
    description: event.content,
    videoUrl,
    thumbnailUrl,
    duration,
    dimensions,
    mimeType,
    isVertical: dimensions ? dimensions.height > dimensions.width : false,
    contentWarning,
    hashtags,
    nipVersion: 'note-video',
    originalEvent: event,
    dTag: undefined,
    videoScheme: 'https',
    streamVariants: [{
      url: videoUrl,
      bitrate: 0,
      mimeType,
      label: 'Default',
      dimensions,
      isHls,
      fallbacks: [],
    }],
  };
}

/** A single stream quality variant parsed from an imeta tag. */
export interface StreamVariant {
  /** Direct URL to the video file for this quality level. */
  url: string;
  /** Bitrate in bits-per-second (0 if not specified). */
  bitrate: number;
  /** MIME type of the stream (e.g. "video/mp4", "application/x-mpegURL"). */
  mimeType?: string;
  /** Human-readable label derived from bitrate (e.g. "4 Mbps"). */
  label: string;
  /** Pixel dimensions, if provided. */
  dimensions?: { width: number; height: number };
  /** Whether this variant is an HLS (m3u8) stream that needs hls.js. */
  isHls: boolean;
  /** Fallback URLs for the same media on other servers. */
  fallbacks: string[];
}

export interface VideoData {
  id: string;
  pubkey: string;
  createdAt: number;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl?: string;
  /** Duration in seconds, if present in the event tags. */
  duration?: number;
  dimensions?: { width: number; height: number };
  mimeType?: string;
  isVertical: boolean;
  contentWarning?: string;
  hashtags: string[];
  nipVersion: NIP71Version;
  originalEvent: NostrEvent;
  /** For addressable events */
  dTag?: string;
  /** The URL scheme of the original video URL before any resolution. */
  videoScheme: string;
  /** All quality variants found in imeta tags, sorted ascending by bitrate. */
  streamVariants: StreamVariant[];
}

/** Known imeta property keys (NIP-92 / NIP-71). */
const IMETA_KEYS = new Set([
  'url', 'm', 'x', 'image', 'dim', 'duration', 'fallback', 'bitrate',
  'blurhash', 'alt', 'service', 'waveform', 'l', 'size',
]);

/** Parse an imeta tag into a key -> values map. */
export function parseImetaTag(tag: string[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const push = (key: string, value: string) => {
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(value);
  };
  for (let i = 1; i < tag.length; i++) {
    const entry = tag[i];
    const spaceIndex = entry.indexOf(' ');
    if (spaceIndex > -1) {
      // Standard form: "url https://…" (space-joined single string)
      push(entry.substring(0, spaceIndex), entry.substring(spaceIndex + 1));
    } else if (IMETA_KEYS.has(entry) && i + 1 < tag.length) {
      // Split form seen in the wild: ["imeta", "url", "https://…", …] —
      // key and value as separate array elements.
      push(entry, tag[i + 1]);
      i++;
    }
  }
  return result;
}

/** Detect the NIP-71 format version of a video event. */
export function detectNIPVersion(event: NostrEvent): NIP71Version {
  const hasImetaTag = event.tags.some(tag => tag[0] === 'imeta');

  if (event.kind === 21 || event.kind === 22) {
    return hasImetaTag ? 'nip71-regular' : 'nip71-legacy';
  }
  if (event.kind === 34235 || event.kind === 34236) {
    return hasImetaTag ? 'nip71-addressable' : 'nip71-legacy';
  }
  return 'nip71-legacy';
}

/** Build a human-readable label from a bitrate value (bits-per-second). */
function bitrateLabel(bps: number): string {
  if (!bps) return 'Unknown';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${Math.round(bps / 1_000)} Kbps`;
  return `${bps} bps`;
}

/** Parse a video event into VideoData. Returns null if no video URL is found. */
export function parseVideoEvent(event: NostrEvent): VideoData | null {
  // Kind 1 notes with linked video files are supported as "note videos"
  if (event.kind === 1) return parseNoteVideoEvent(event);

  if (!ALL_VIDEO_KINDS.includes(event.kind)) return null;

  const nipVersion = detectNIPVersion(event);

  const title = event.tags.find(t => t[0] === 'title')?.[1] || 'Untitled Video';
  const description = event.content || '';
  const contentWarning = event.tags.find(t => t[0] === 'content-warning')?.[1];
  const hashtags = event.tags.filter(t => t[0] === 't').map(t => t[1]);
  const dTag = event.tags.find(t => t[0] === 'd')?.[1];

  // Addressable events must have a d tag — reject spoofed/malformed ones
  if ((event.kind === 34235 || event.kind === 34236) && !dTag) return null;

  let videoUrl = '';
  let thumbnailUrl: string | undefined;
  let duration: number | undefined;
  let dimensions: { width: number; height: number } | undefined;
  let mimeType: string | undefined;
  let streamVariants: StreamVariant[] = [];
  let rawVideoUrl = '';

  const imetaTags = event.tags.filter(t => t[0] === 'imeta');

  if (imetaTags.length > 0) {
    const parsed: StreamVariant[] = [];
    for (const tag of imetaTags) {
      const imeta = parseImetaTag(tag);
      const url = imeta.url?.[0];
      if (!url) continue;

      const bitrate = imeta.bitrate?.[0] ? parseInt(imeta.bitrate[0], 10) : 0;
      const variantMime = imeta.m?.[0];
      let variantDim: { width: number; height: number } | undefined;
      if (imeta.dim?.[0]) {
        const [w, h] = imeta.dim[0].split('x').map(Number);
        if (w && h) variantDim = { width: w, height: h };
      }

      parsed.push({
        url: resolveMediaUrl(url),
        bitrate,
        mimeType: variantMime,
        label: bitrateLabel(bitrate),
        dimensions: variantDim,
        isHls: isHlsUrl(url) || isHlsMimeType(variantMime),
        fallbacks: (imeta.fallback ?? []).map(resolveMediaUrl),
      });

      // Use data from the first imeta tag for backward-compat fields
      if (!videoUrl) {
        rawVideoUrl = url;
        videoUrl = resolveMediaUrl(url);
        thumbnailUrl = imeta.image?.[0];
        mimeType = variantMime;
        if (imeta.duration?.[0]) {
          const d = parseFloat(imeta.duration[0]);
          if (Number.isFinite(d) && d > 0) duration = d;
        }
        if (variantDim) dimensions = variantDim;
      }
    }

    // Sort variants ascending by bitrate so index 0 = lowest quality
    parsed.sort((a, b) => a.bitrate - b.bitrate);
    streamVariants = parsed;

    if (streamVariants.length > 0) {
      // Default to highest quality as the base videoUrl
      videoUrl = streamVariants[streamVariants.length - 1].url;
      if (!duration) {
        // Check other imeta tags for a duration value
        for (const tag of imetaTags) {
          const imeta = parseImetaTag(tag);
          if (imeta.duration?.[0]) {
            const d = parseFloat(imeta.duration[0]);
            if (Number.isFinite(d) && d > 0) { duration = d; break; }
          }
        }
      }
    }
  } else {
    // Legacy format (NIP-71 v1): separate tags instead of imeta
    videoUrl = event.tags.find(t => t[0] === 'url')?.[1] || '';
    rawVideoUrl = videoUrl;
    thumbnailUrl = event.tags.find(t => t[0] === 'thumb')?.[1] ||
                   event.tags.find(t => t[0] === 'image')?.[1];
    mimeType = event.tags.find(t => t[0] === 'm')?.[1];

    const durationTag = event.tags.find(t => t[0] === 'duration')?.[1];
    if (durationTag) {
      const d = parseFloat(durationTag);
      if (Number.isFinite(d) && d > 0) duration = d;
    }

    const dimTag = event.tags.find(t => t[0] === 'dim')?.[1];
    if (dimTag) {
      const [w, h] = dimTag.split('x').map(Number);
      if (w && h) dimensions = { width: w, height: h };
    }
  }

  // Some events carry the video URL only in the content — scan it as a
  // last resort (same detection as kind 1 note videos).
  if (!videoUrl) {
    const found = extractVideoUrlFromText(event.content);
    if (found) {
      videoUrl = found;
      rawVideoUrl = found;
    }
  }

  if (!videoUrl) return null;

  const schemeMatch = (rawVideoUrl || videoUrl).match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  const videoScheme = schemeMatch ? schemeMatch[1].toLowerCase() : 'https';

  videoUrl = resolveMediaUrl(videoUrl);

  if (streamVariants.length === 0) {
    streamVariants = [{
      url: videoUrl,
      bitrate: 0,
      mimeType,
      label: 'Default',
      dimensions,
      isHls: isHlsUrl(videoUrl) || isHlsMimeType(mimeType),
      fallbacks: [],
    }];
  }

  let isVertical = event.kind === 22 || event.kind === 34236;
  if (dimensions) {
    isVertical = dimensions.height > dimensions.width;
  }

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    title,
    description,
    videoUrl,
    thumbnailUrl,
    duration,
    dimensions,
    mimeType,
    isVertical,
    contentWarning,
    hashtags,
    nipVersion,
    originalEvent: event,
    dTag,
    videoScheme,
    streamVariants,
  };
}

// ── URL type detection ─────────────────────────────────────────────────────

/** Returns true if the URL points to an HLS stream (m3u8). */
export function isHlsUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0].split('#')[0];
  return lower.endsWith('.m3u8') || lower.endsWith('.m3u');
}

/** Returns true if the MIME type indicates an HLS stream. */
export function isHlsMimeType(mimeType: string | undefined): boolean {
  if (!mimeType) return false;
  const lower = mimeType.toLowerCase();
  return lower === 'application/x-mpegurl' || lower === 'application/vnd.apple.mpegurl';
}

/** Returns true if the URL points to a YouTube video. */
export function isYouTubeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      /(?:^|\.)youtube\.com$/i.test(u.hostname) ||
      /(?:^|\.)youtube-nocookie\.com$/i.test(u.hostname) ||
      /(?:^|\.)youtu\.be$/i.test(u.hostname) ||
      /(?:^|\.)yt\.be$/i.test(u.hostname)
    );
  } catch {
    return false;
  }
}

/** Extracts the YouTube video ID from a YouTube URL. */
export function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (/(?:^|\.)youtu\.be$/i.test(u.hostname) || /(?:^|\.)yt\.be$/i.test(u.hostname)) {
      const id = u.pathname.slice(1).split('/')[0];
      return id || null;
    }
    const v = u.searchParams.get('v');
    if (v) return v;
    const m = u.pathname.match(/\/(?:embed|shorts|live|v)\/([^/?#&]+)/);
    if (m) return m[1];
    return null;
  } catch {
    return null;
  }
}

/** Returns true if the URL uses the ipfs:// protocol scheme. */
export function isIpfsUrl(url: string): boolean {
  return url.startsWith('ipfs://');
}

/** Default IPFS gateway to use when converting ipfs:// URLs. */
export const IPFS_GATEWAY = 'https://ipfs.io/ipfs/';

/** Converts an ipfs:// URL to a plain HTTPS URL via the public IPFS gateway. */
export function resolveIpfsUrl(url: string): string {
  if (!isIpfsUrl(url)) return url;
  const cid = url.slice('ipfs://'.length);
  return `${IPFS_GATEWAY}${cid}`;
}

/** Returns true if the URL uses the blossom: protocol scheme (BUD-10). */
export function isBlossomUrl(url: string): boolean {
  return url.startsWith('blossom:');
}

/** Default Blossom server used to resolve blossom: URIs (BUD-10). */
export const DEFAULT_BLOSSOM_SERVER = 'https://blossom.ditto.pub/';

/**
 * Converts a blossom:<sha256>[.ext] URI (BUD-10) to an HTTPS URL on the
 * default Blossom server. Non-blossom URLs are returned unchanged.
 */
export function resolveBlossomUrl(url: string): string {
  if (!isBlossomUrl(url)) return url;
  const hashAndExt = url.slice('blossom:'.length);
  return `${DEFAULT_BLOSSOM_SERVER.replace(/\/?$/, '/')}${hashAndExt}`;
}

/** Resolve non-HTTP media schemes (ipfs:, blossom:) to plain HTTPS URLs. */
export function resolveMediaUrl(url: string): string {
  return resolveBlossomUrl(resolveIpfsUrl(url));
}

/** Format duration from seconds to M:SS or H:MM:SS. */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Get the NIP version display label. */
export function getNIPVersionLabel(version: NIP71Version): string {
  switch (version) {
    case 'nip71-regular':
      return 'Regular (kind 21/22)';
    case 'nip71-addressable':
      return 'Addressable (kind 34235/34236)';
    case 'nip71-legacy':
      return 'Legacy (no imeta)';
    case 'note-video':
      return 'Note (kind 1)';
  }
}

/** Check if a video can be edited (only addressable events can be edited). */
export function isVideoEditable(video: VideoData): boolean {
  return video.originalEvent.kind === 34235 || video.originalEvent.kind === 34236;
}

/** Get the address (a-tag value) for an addressable video. */
export function getVideoAddress(video: VideoData): string | null {
  if (!video.dTag) return null;
  return `${video.originalEvent.kind}:${video.pubkey}:${video.dTag}`;
}

/** plebs.app tagged every upload with this hashtag across all event kinds. */
export const PLEBS_TAG = 'pv69420';

/** True when the video carries the plebs.app hashtag. */
export function isPlebsVideo(video: VideoData): boolean {
  return video.hashtags.some(t => t.toLowerCase() === PLEBS_TAG);
}

/**
 * Dedupe plebs.app uploads: each video was published as up to three events
 * (addressable, regular, kind 1 note) sharing the same video URL. Keep one
 * entry per video URL — preferring addressable, then regular, then legacy,
 * then the note. Videos where only one event survived are kept as-is.
 */
export function dedupePlebsVideos(videos: VideoData[]): VideoData[] {
  const priority = (v: VideoData): number => {
    switch (v.nipVersion) {
      case 'nip71-addressable': return 0;
      case 'nip71-regular': return 1;
      case 'nip71-legacy': return 2;
      case 'note-video': return 3;
    }
  };

  const byUrl = new Map<string, VideoData>();
  for (const video of videos) {
    const key = video.videoUrl;
    const existing = byUrl.get(key);
    if (
      !existing ||
      priority(video) < priority(existing) ||
      (priority(video) === priority(existing) && video.createdAt > existing.createdAt)
    ) {
      byUrl.set(key, video);
    }
  }
  return Array.from(byUrl.values()).sort((a, b) => b.createdAt - a.createdAt);
}

/** Time ago formatter. */
export function timeAgo(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
  return `${Math.floor(diff / 31536000)}y ago`;
}

/**
 * Get the best NIP-19 identifier for linking to a video:
 * naddr for addressable events, nevent for regular ones.
 */
export function getVideoNip19(video: VideoData, relayHints: string[] = []): string {
  if (video.dTag && (video.originalEvent.kind === 34235 || video.originalEvent.kind === 34236)) {
    return nip19.naddrEncode({
      kind: video.originalEvent.kind,
      pubkey: video.pubkey,
      identifier: video.dTag,
      relays: relayHints.length > 0 ? relayHints : undefined,
    });
  }
  return nip19.neventEncode({
    id: video.id,
    author: video.pubkey,
    kind: video.originalEvent.kind,
    relays: relayHints.length > 0 ? relayHints : undefined,
  });
}
